/**
 * UpdateSheet — "What's new in CampusHub <version>", in two situations:
 *
 *   • mode="update"    — the native update gate: a build the school can no
 *                        longer run on ("forced", non-dismissible — no Later)
 *                        or, if a caller passes `onLater`, a dismissible one.
 *                        Primary "Update now" opens the store listing.
 *   • mode="whats-new" — once after an update has landed, for the version now
 *                        running (release-notes.ts). Primary "Got it".
 *
 * A bottom sheet in the app (isAppLike), a centred modal on the web. The
 * header is real art — Fluent Emoji 3D objects (MIT, public/images/icons/3d)
 * — with a short CSS entrance (rise + float-in) that `prefers-reduced-motion`
 * switches off. Focus is trapped in the dialog, starts on the primary action,
 * and Escape does what "Later"/"Got it" does when the sheet is dismissible.
 */
import { useCallback, useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from "react";
import { useTranslation } from "react-i18next";
import { isAppLike } from "@/lib/platform";
import type { ReleaseEntry, ReleaseNote } from "@/release-notes";
import "./update-sheet.css";

/**
 * Shown when the running (old) bundle has no notes for the version on offer.
 * A function (not a module-level const) so the copy re-evaluates against the
 * current language — per-release notes (`entry.notes`) are bespoke changelog
 * text from `release-notes.ts` and are NOT translated here; only this
 * generic fallback is.
 */
function genericNotes(t: (key: string, opts?: Record<string, unknown>) => string): readonly ReleaseNote[] {
  return [
    { icon: "rocket", title: t("common:updateSheet.fasterTitle", { defaultValue: "Faster and smoother" }), body: t("common:updateSheet.fasterBody", { defaultValue: "Screens open quicker and scroll more smoothly." }) },
    { icon: "sparkles", title: t("common:updateSheet.improvedTitle", { defaultValue: "New and improved" }), body: t("common:updateSheet.improvedBody", { defaultValue: "Fresh features and a cleaner look across the app." }) },
    { icon: "wrench", title: t("common:updateSheet.fixesTitle", { defaultValue: "Fixes and security" }), body: t("common:updateSheet.fixesBody", { defaultValue: "Important fixes that keep your school's data safe." }) },
  ];
}

export interface UpdateSheetProps {
  mode: "update" | "whats-new";
  /** The version the notes are for — "2.0.73". Null → "the latest version". */
  version: string | null;
  /** Notes for that version (already role-filtered); null → generic notes. */
  entry: ReleaseEntry | null;
  onPrimary: () => void;
  /** Present → the sheet can be dismissed ("Later", Escape, backdrop). */
  onLater?: () => void;
}

const icon = (name: string) => `/images/icons/3d/${name}.webp`;

export function UpdateSheet({ mode, version, entry, onPrimary, onLater }: UpdateSheetProps) {
  const { t } = useTranslation();
  const sheet = isAppLike();
  const dialogRef = useRef<HTMLDivElement>(null);
  const primaryRef = useRef<HTMLButtonElement>(null);
  const notes = entry?.notes ?? genericNotes(t);
  const title = version
    ? t("common:updateSheet.titleWithVersion", { version, defaultValue: `What's new in CampusHub ${version}` })
    : t("common:updateSheet.titleNoVersion", { defaultValue: "What's new in CampusHub" });
  const headline = entry?.headline
    ?? (mode === "update"
      ? t("common:updateSheet.updateHeadline", { defaultValue: "A new version is ready. Update to keep everything working smoothly." })
      : t("common:updateSheet.whatsNewHeadline", { defaultValue: "You're on the latest version." }));
  const dismiss = mode === "whats-new" ? onPrimary : onLater;

  useEffect(() => {
    primaryRef.current?.focus();
  }, []);

  // The notes scroll on their own between a fixed header and an opaque
  // footer; a soft fade at the bottom edge says "more below" until the
  // reader reaches the end.
  const scrollRef = useRef<HTMLDivElement>(null);
  const [moreBelow, setMoreBelow] = useState(false);
  const measure = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setMoreBelow(el.scrollHeight - el.scrollTop - el.clientHeight > 4);
  }, []);
  useEffect(() => {
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [measure]);

  // A focus trap: Tab and Shift+Tab cycle inside the dialog.
  const onKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Escape" && dismiss) {
      e.preventDefault();
      dismiss();
      return;
    }
    if (e.key !== "Tab" || !dialogRef.current) return;
    const focusables = [...dialogRef.current.querySelectorAll<HTMLElement>("button:not([disabled]), a[href]")];
    if (focusables.length === 0) return;
    const first = focusables[0]!;
    const last = focusables[focusables.length - 1]!;
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  return (
    <div className={`us-root${sheet ? " us-root--sheet" : " us-root--modal"}`} data-testid="update-sheet">
      <div className="us-backdrop" onClick={dismiss} aria-hidden />
      <div
        ref={dialogRef}
        className="us-dialog"
        role={dismiss ? "dialog" : "alertdialog"}
        aria-modal="true"
        aria-labelledby="us-title"
        aria-describedby="us-headline"
        onKeyDown={onKeyDown}
      >
        <div
          ref={scrollRef}
          className={`us-scroll${moreBelow ? " us-scroll--more" : ""}`}
          onScroll={measure}
          data-testid="update-sheet-scroll"
        >
        {sheet ? <span className="us-grab" aria-hidden /> : null}
        <div className="us-hero" aria-hidden>
          <img className="us-hero__main" src={icon(mode === "update" ? "rocket" : "party-popper")} alt="" width={96} height={96} />
          <img className="us-hero__spark us-hero__spark--a" src={icon("sparkles")} alt="" width={34} height={34} />
          <img className="us-hero__spark us-hero__spark--b" src={icon("sparkles")} alt="" width={24} height={24} />
        </div>
        <p className="us-eyebrow">{mode === "update" ? t("common:updateSheet.updateAvailable", { defaultValue: "Update available" }) : t("common:updateSheet.updated", { defaultValue: "Updated" })}</p>
        <h2 className="us-title" id="us-title">{title}</h2>
        <p className="us-headline" id="us-headline">{headline}</p>

        <ul className="us-notes">
          {notes.map((n) => (
            <li key={n.title} className="us-note">
              <img className="us-note__ico" src={icon(n.icon)} alt="" width={36} height={36} decoding="async" onLoad={measure} />
              <span className="us-note__text">
                <span className="us-note__title">{n.title}</span>
                <span className="us-note__body">{n.body}</span>
              </span>
            </li>
          ))}
        </ul>
        </div>

        <div className="us-actions">
          <button ref={primaryRef} type="button" className="us-btn us-btn--primary" onClick={onPrimary}>
            {mode === "update" ? t("common:updateSheet.updateNow", { defaultValue: "Update now" }) : t("dailyBrief:gotIt", { defaultValue: "Got it" })}
          </button>
          {mode === "update" && onLater ? (
            <button type="button" className="us-btn us-btn--ghost" onClick={onLater}>{t("common:updateSheet.later", { defaultValue: "Later" })}</button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
